import { User } from "@auth0/auth0-react";
import { z } from "zod";

import {
  CompanySimpleUserToGrantsItem,
  CompanySimpleUserToRole,
} from "@/generated/api/users";

import { logger } from "./logger";

type UserState = "complete" | "incomplete";

const getUserState = (user: User): UserState => {
  try {
    getUser(user);
    return "complete";
  } catch {
    return "incomplete";
  }
};

const phoneSchema = z.object({
  countryCode: z.string(),
  phoneNumber: z.string(),
});

const commonSchema = z.object({
  phone: phoneSchema,
});

const irishPhoneSchema = phoneSchema.extend({
  countryCode: z.literal("+353"),
});

const irishCommonSchema = z.object({
  phone: irishPhoneSchema,
});

const settingsSchema = z
  .object({
    language: z.string(),
    news: z
      .object({
        language: z.string(),
      })
      .optional(),
  })
  .optional();

const irishCANSchema = z.object({
  companies: z.null().or(
    z.array(
      z.object({
        companyNumber: z.string(),
        countryCode: z.string(),
      }),
    ),
  ),
  settings: settingsSchema,
});

const CANSchema = z.object({
  companies: z.array(
    z.object({
      companyNumber: z.string(),
      countryCode: z.string(),
    }),
  ),
  settings: settingsSchema,
});

const incompleteCANSchema = z
  .object({
    companies: z
      .array(
        z.object({
          companyNumber: z.string(),
          countryCode: z.string(),
        }),
      )
      .or(z.null()),
    settings: settingsSchema,
  })
  .partial();

const userMetadataSchema = z.object({
  can: CANSchema,
  common: commonSchema,
});

const incompleteUserMetadataSchema = z
  .object({
    can: incompleteCANSchema,
    common: commonSchema,
  })
  .partial();

const irishUserMetadataSchema = z.object({
  can: irishCANSchema,
  common: irishCommonSchema,
});

const planTypeSchema = z.union([
  z.literal("PLUS"),
  z.literal("BASIC"),
  z.literal("PREMIUM"),
  z.literal("TRADER"),
]);

const companySchema = z.object({
  companyId: z.number(),
  companyName: z.string(),
  companyNumber: z
    .string()
    .nullable()
    .transform((v) => v ?? undefined),
  countryCode: z
    .string()
    .nullable()
    .transform((v) => v ?? undefined),
});

const appMetadataSchema = z
  .object({
    companies: z.array(companySchema).nullish(),
    events: z.object({
      // We effectively use this to tell whether the user has been reset. If they have been reset, `registeredAt` field has been reset as well
      // and thus the zod check will throw.
      registeredAt: z.number(),
    }),
  })
  .nullish();

const incompleteAppMetadataSchema = z
  .object({
    companies: z.array(companySchema).nullish(),
    events: z.object({
      // We effectively use this to tell whether the user has been reset. If they have been reset, `registeredAt` field has been reset as well
      // and thus the zod check will throw.
      registeredAt: z.number().nullish(),
    }),
  })
  .partial()
  .nullish();

const irishAppMetadataSchema = z
  .object({
    companies: z.array(companySchema).nullish(),
    events: z.object({
      // We effectively use this to tell whether the user has been reset. If they have been reset, `registeredAt` field has been reset as well
      // and thus the zod check will throw.
      registeredAt: z.number().nullish(),
    }),
  })
  .nullish();

const userIdentitySchema = z.object({
  email: z.string(),
  name: z.string(),
});

const userSchema = z
  .object({
    "can/app_metadata": appMetadataSchema,
    user_metadata: userMetadataSchema,
  })
  .extend(userIdentitySchema.shape);

const incompleteUserSchema = z
  .object({
    "can/app_metadata": incompleteAppMetadataSchema,
    user_metadata: incompleteUserMetadataSchema,
  })
  .partial()
  .extend(userIdentitySchema.shape);

const irishUserSchema = z
  .object({
    "can/app_metadata": irishAppMetadataSchema,
    user_metadata: irishUserMetadataSchema,
  })
  .extend(userIdentitySchema.shape);

type UserSchema = z.infer<typeof userSchema | typeof irishUserSchema>;
type IncompleteUserSchema = z.infer<typeof incompleteUserSchema>;
type Role = CompanySimpleUserToRole;
type PlanSchema = { planId: number; type: z.infer<typeof planTypeSchema> };
type Grant = CompanySimpleUserToGrantsItem;

const getUser = (user: User): UserSchema => {
  if (isIrishUser(user)) {
    return irishUserSchema.parse(user);
  }
  return userSchema.parse(user);
};

const getMaybeUser = (user: User) => {
  if (isIrishUser(user)) {
    try {
      return irishUserSchema.parse(user);
    } catch (e) {
      logger.setContext(user);
      throw e;
    }
  }

  return incompleteUserSchema.parse(user);
};

/**
 * Irish users are the only ones in the world that are not required to be member of any company.
 */
const isIrishUser = (user: User) =>
  z
    .object({ user_metadata: irishUserMetadataSchema.omit({ can: true }) })
    .safeParse(user).success;

export { getMaybeUser, getUser, getUserState, planTypeSchema };
export type { Grant, IncompleteUserSchema, PlanSchema, Role, UserSchema };
